<template>
  <div class="configuracion-plantillas py-4" :class="themeClass">
    <div class="container-fluid">
      <div class="row justify-content-center">
        <div class="col-xxl-9 col-xl-10">
          <div class="card shadow-sm border-0">
            <div class="card-header bg-gradient text-white">
              <h3 class="mb-0"><i class="bi bi-file-earmark-richtext me-2"></i>Plantillas de Documentos</h3>
              <small class="text-white-50">Personaliza los mensajes y contenido que aparecen en tus comprobantes enviados por correo</small>
            </div>
            <div class="card-body">

              <div v-if="cargando" class="text-center py-5">
                <div class="spinner-border text-primary"></div>
                <p class="text-muted mt-3">Cargando plantillas...</p>
              </div>

              <template v-else>
                <div v-if="mensajeError" class="alert alert-danger d-flex justify-content-between align-items-center">
                  <span>{{ mensajeError }}</span>
                  <button type="button" class="btn-close" @click="mensajeError = ''"></button>
                </div>
                <div v-if="mensajeExito" class="alert alert-success d-flex justify-content-between align-items-center">
                  <span>{{ mensajeExito }}</span>
                  <button type="button" class="btn-close" @click="mensajeExito = ''"></button>
                </div>

                <!-- Selector de tipo de documento -->
                <div class="mb-4 d-flex gap-2 flex-wrap">
                  <button
                    v-for="tipo in tiposDocumento"
                    :key="tipo.clave"
                    class="btn"
                    :class="tipoActivo === tipo.clave ? 'btn-primary' : 'btn-outline-secondary'"
                    @click="tipoActivo = tipo.clave"
                  >
                    <i :class="tipo.icono + ' me-1'"></i>{{ tipo.etiqueta }}
                  </button>
                </div>

                <!-- Editor de plantilla activa -->
                <div v-for="tipo in tiposDocumento" :key="tipo.clave" v-show="tipoActivo === tipo.clave">
                  <section class="section-card">
                    <header class="section-card__header">
                      <div>
                        <h5 class="mb-0">Plantilla para {{ tipo.etiqueta }}</h5>
                        <small class="text-muted">Define el asunto y cuerpo del correo que recibe el cliente al emitir este documento</small>
                      </div>
                    </header>
                    <div class="section-card__body">
                      <div class="row g-3">
                        <div class="col-12">
                          <label class="form-label">Asunto del correo</label>
                          <input type="text" class="form-control" v-model.trim="plantillas[tipo.clave].asunto"
                                 :placeholder="tipo.asuntoPorDefecto" />
                          <small class="text-muted">Variables disponibles: <code>{empresa}</code>, <code>{numero}</code>, <code>{fecha}</code>, <code>{cliente}</code></small>
                        </div>
                        <div class="col-12">
                          <label class="form-label">Cuerpo del correo</label>
                          <textarea class="form-control" rows="8" v-model.trim="plantillas[tipo.clave].cuerpo"
                                    :placeholder="tipo.cuerpoPorDefecto"></textarea>
                          <small class="text-muted">Variables disponibles: <code>{empresa}</code>, <code>{numero}</code>, <code>{fecha}</code>, <code>{cliente}</code>, <code>{total}</code>, <code>{link_xml}</code>, <code>{link_pdf}</code></small>
                        </div>
                        <div class="col-12">
                          <div class="form-check form-switch">
                            <input class="form-check-input" type="checkbox"
                                   :id="`adjuntarPdf-${tipo.clave}`"
                                   v-model="plantillas[tipo.clave].adjuntarPdf">
                            <label class="form-check-label" :for="`adjuntarPdf-${tipo.clave}`">
                              Adjuntar PDF del documento al correo
                            </label>
                          </div>
                        </div>
                        <div class="col-12">
                          <div class="form-check form-switch">
                            <input class="form-check-input" type="checkbox"
                                   :id="`adjuntarXml-${tipo.clave}`"
                                   v-model="plantillas[tipo.clave].adjuntarXml">
                            <label class="form-check-label" :for="`adjuntarXml-${tipo.clave}`">
                              Adjuntar XML firmado al correo
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>

                  <!-- Vista previa -->
                  <section class="section-card">
                    <header class="section-card__header">
                      <div>
                        <h5 class="mb-0">Vista previa</h5>
                        <small class="text-muted">Así verá el destinatario el correo (con datos de ejemplo)</small>
                      </div>
                    </header>
                    <div class="section-card__body">
                      <div class="preview-email border rounded p-4">
                        <div class="mb-2">
                          <strong>Asunto:</strong>
                          <span class="ms-1">{{ renderizarPlantilla(plantillas[tipo.clave].asunto, tipo.clave) }}</span>
                        </div>
                        <hr>
                        <pre class="preview-body mb-0">{{ renderizarPlantilla(plantillas[tipo.clave].cuerpo, tipo.clave) }}</pre>
                      </div>
                    </div>
                  </section>
                </div>

                <div class="d-flex justify-content-end gap-2 mt-2">
                  <button class="btn btn-outline-secondary" @click="restablecerPorDefecto">
                    <i class="bi bi-arrow-counterclockwise me-1"></i>Restablecer por defecto
                  </button>
                  <button class="btn btn-primary px-4" @click="guardar" :disabled="guardando">
                    <span v-if="guardando" class="spinner-border spinner-border-sm me-2"></span>
                    Guardar plantillas
                  </button>
                </div>

              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import themeManager from '@/store/theme'
import api from '@/services/api'

const VARIABLES_EJEMPLO = {
  empresa: 'Comercial Demo S.A.',
  numero:  '001-001-000000123',
  fecha:   '17/03/2026',
  cliente: 'Juan Pérez',
  total:   '$250.00',
  link_xml: 'https://sicecom.com/documentos/xml/...',
  link_pdf: 'https://sicecom.com/documentos/pdf/...'
}

const PLANTILLAS_POR_DEFECTO = {
  factura: {
    asunto: 'Factura Electrónica {numero} - {empresa}',
    cuerpo: `Estimado/a {cliente},\n\nAdjuntamos su factura electrónica N.° {numero} emitida el {fecha} por un total de {total}.\n\nPuede descargar su documento en los siguientes enlaces:\n- PDF: {link_pdf}\n- XML: {link_xml}\n\nGracias por su preferencia.\n\n{empresa}`,
    adjuntarPdf: true,
    adjuntarXml: true
  },
  nota_credito: {
    asunto: 'Nota de Crédito {numero} - {empresa}',
    cuerpo: `Estimado/a {cliente},\n\nAdjuntamos la Nota de Crédito N.° {numero} emitida el {fecha}.\n\n- PDF: {link_pdf}\n- XML: {link_xml}\n\n{empresa}`,
    adjuntarPdf: true,
    adjuntarXml: true
  },
  guia_remision: {
    asunto: 'Guía de Remisión {numero} - {empresa}',
    cuerpo: `Estimado/a {cliente},\n\nAdjuntamos la Guía de Remisión N.° {numero} emitida el {fecha}.\n\n- PDF: {link_pdf}\n- XML: {link_xml}\n\n{empresa}`,
    adjuntarPdf: true,
    adjuntarXml: false
  }
}

export default {
  name: 'ClientConfiguracionPlantillas',
  data() {
    return {
      cargando: true,
      guardando: false,
      mensajeError: '',
      mensajeExito: '',
      tipoActivo: 'factura',
      plantillas: JSON.parse(JSON.stringify(PLANTILLAS_POR_DEFECTO)),
      tiposDocumento: [
        { clave: 'factura',      etiqueta: 'Factura',          icono: 'bi bi-receipt',   asuntoPorDefecto: PLANTILLAS_POR_DEFECTO.factura.asunto,      cuerpoPorDefecto: PLANTILLAS_POR_DEFECTO.factura.cuerpo },
        { clave: 'nota_credito', etiqueta: 'Nota de Crédito',  icono: 'bi bi-file-minus',asuntoPorDefecto: PLANTILLAS_POR_DEFECTO.nota_credito.asunto,  cuerpoPorDefecto: PLANTILLAS_POR_DEFECTO.nota_credito.cuerpo },
        { clave: 'guia_remision',etiqueta: 'Guía de Remisión', icono: 'bi bi-truck',     asuntoPorDefecto: PLANTILLAS_POR_DEFECTO.guia_remision.asunto, cuerpoPorDefecto: PLANTILLAS_POR_DEFECTO.guia_remision.cuerpo }
      ]
    }
  },
  computed: {
    themeClass() {
      return themeManager.themeClass
    }
  },
  created() {
    this.cargarDatos()
  },
  methods: {
    async cargarDatos() {
      this.cargando = true
      try {
        const res = await api.get('/configuracion/plantillas')
        const datos = res?.data?.data || res?.data || {}
        if (datos && typeof datos === 'object') {
          Object.keys(PLANTILLAS_POR_DEFECTO).forEach(clave => {
            if (datos[clave]) {
              this.plantillas[clave] = { ...PLANTILLAS_POR_DEFECTO[clave], ...datos[clave] }
            }
          })
        }
      } catch {
        // Usa las plantillas por defecto si no hay datos guardados
      } finally {
        this.cargando = false
      }
    },

    renderizarPlantilla(texto) {
      if (!texto) return ''
      return Object.entries(VARIABLES_EJEMPLO).reduce((acc, [clave, valor]) => {
        return acc.replace(new RegExp(`\\{${clave}\\}`, 'g'), valor)
      }, texto)
    },

    restablecerPorDefecto() {
      if (!confirm('¿Restablecer la plantilla al texto por defecto?')) return
      this.plantillas[this.tipoActivo] = JSON.parse(JSON.stringify(PLANTILLAS_POR_DEFECTO[this.tipoActivo]))
    },

    async guardar() {
      this.guardando = true
      this.mensajeError = ''
      this.mensajeExito = ''
      try {
        await api.post('/configuracion/plantillas', this.plantillas)
        this.mensajeExito = 'Plantillas guardadas correctamente.'
      } catch (error) {
        this.mensajeError = error?.response?.data?.error || error?.message || 'No se pudo guardar las plantillas.'
      } finally {
        this.guardando = false
      }
    }
  }
}
</script>

<style scoped>
.configuracion-plantillas {
  --primary-color: #1173d4;
  --body-bg: #f5f7fb;
  --card-bg: #ffffff;
  --border-color: rgba(17, 115, 212, 0.1);
  --text-primary: #1f2933;
  --text-secondary: #4a5568;
  background-color: var(--body-bg);
  min-height: 100vh;
  padding-bottom: 2rem;
}

.configuracion-plantillas.dark-theme {
  --body-bg: #0b1320;
  --card-bg: #111d30;
  --border-color: rgba(148, 163, 184, 0.18);
  --text-primary: #e2e8f0;
  --text-secondary: #cbd5f5;
}

.card {
  background-color: var(--card-bg);
  border-color: var(--border-color);
  color: var(--text-primary);
}

.bg-gradient {
  background: linear-gradient(135deg, rgba(17, 115, 212, 0.95) 0%, rgba(40, 137, 235, 0.9) 100%);
}

.section-card {
  border: 1px solid var(--border-color);
  border-radius: 18px;
  padding: 1.6rem;
  margin-bottom: 1.4rem;
  background-color: var(--card-bg);
  color: var(--text-primary);
  box-shadow: 0 10px 24px -18px rgba(15, 46, 70, 0.28);
}

.configuracion-plantillas.dark-theme .section-card { box-shadow: none; }

.section-card__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.2rem;
}

.section-card__header h5 { font-weight: 700; }

.form-control,
.form-select,
textarea {
  background-color: var(--card-bg);
  color: var(--text-primary);
  border-color: rgba(148, 163, 184, 0.4);
  border-radius: 10px;
}

.form-control:focus, .form-select:focus, textarea:focus {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(17, 115, 212, 0.15);
  background-color: var(--card-bg);
}

.configuracion-plantillas.dark-theme .form-control,
.configuracion-plantillas.dark-theme .form-select,
.configuracion-plantillas.dark-theme textarea {
  background-color: rgba(15, 23, 42, 0.6);
  border-color: rgba(148, 163, 184, 0.3);
  color: var(--text-primary);
}

.preview-email {
  background-color: #f8f9fa;
  border-color: var(--border-color) !important;
  color: var(--text-primary);
}

.configuracion-plantillas.dark-theme .preview-email {
  background-color: rgba(15, 23, 42, 0.4);
}

.preview-body {
  white-space: pre-wrap;
  font-family: inherit;
  font-size: 0.9em;
  color: var(--text-secondary);
}

code {
  background: rgba(17, 115, 212, 0.08);
  padding: 1px 5px;
  border-radius: 4px;
  font-size: 0.85em;
}
</style>
